import { useEffect, useRef, useState } from 'react';
import { Smartphone, GitBranch, Database, Shield, Settings, Globe, ArrowRight } from 'lucide-react';

const features = [
  {
    icon: GitBranch,
    title: 'Smart Router',
    description: 'The moat. NexGenPay\'s Smart Router automatically selects the optimal payment rail per transaction in under 100ms — FedNow, RTP, Stablecoin, or Tokenized Deposits. 5/5 unit tests passing. 100% decisions audit-logged. One integration replaces your entire multi-vendor stack.',
    tag: 'Core Engine',
    span: 'lg:col-span-2',
    size: 'large',
  },
  {
    icon: Smartphone,
    title: 'White-Label eWallet',
    description: 'Your bank\'s brand on every screen. Full UI customization, your domain, zero NexGenPay attribution. Complete ownership of the customer experience.',
    tag: 'Brand Sovereignty',
    span: 'lg:col-span-1',
    size: 'small',
  },
  {
    icon: Globe,
    title: 'Four Payment Rails',
    description: 'FedNow instant settlement, RTP ($10M limit), Stablecoin transfers, and Tokenized Deposits (4.25% APY). The only platform covering all four.',
    tag: 'Infrastructure',
    span: 'lg:col-span-1',
    size: 'small',
  },
  {
    icon: Shield,
    title: 'Examiner-Ready Compliance',
    description: 'OFAC screening, BSA/AML enforcement, Travel Rule compliance, and GENIUS Act-ready architecture — built in from day one, not bolted on. Every decision audit-logged and examiner-readable.',
    tag: 'Regulatory',
    span: 'lg:col-span-2',
    size: 'large',
  },
  {
    icon: Settings,
    title: 'Bank Admin Console',
    description: 'Operational sovereignty. Your team controls every parameter — limits, rails, compliance thresholds, customer tiers — from a single dashboard.',
    tag: 'Operations',
    span: 'lg:col-span-1',
    size: 'small',
  },
  {
    icon: Database,
    title: 'Open API Gateway',
    description: 'Pre-built connectors for Fiserv, Jack Henry, FIS, and Finastra. REST API with webhook support. Deploy in weeks, not years.',
    tag: 'Integration',
    span: 'lg:col-span-1',
    size: 'small',
  },
];

export default function FeaturesSection() {
  const sectionRef = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setRevealed(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="features"
      data-testid="features-section"
      ref={sectionRef}
      className="relative section-padding bg-black grid-bg"
      style={{ scrollMarginTop: '80px' }}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6"
          style={{
            opacity: revealed ? 1 : 0,
            transform: revealed ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.7s ease',
          }}>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="green-line" />
              <span className="text-[#00FF00] font-mono text-xs uppercase tracking-widest">Platform Features</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-black uppercase text-white leading-tight"
              style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Everything Your Bank Needs to
              <br />
              <span className="text-[#00FF00]">Own the Digital Layer</span>
            </h2>
          </div>
          <p className="text-[#A3A3A3] text-sm max-w-xs leading-relaxed md:text-right" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Purpose-built for community banks, regional banks, and credit unions with $500M–$10B in assets.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                data-testid={`feature-card-${i}`}
                className={`nx-card group relative overflow-hidden ${feature.span}`}
                style={{
                  padding: feature.size === 'large' ? '40px' : '32px',
                  opacity: revealed ? 1 : 0,
                  transform: revealed ? 'translateY(0)' : 'translateY(40px)',
                  transition: `all 0.6s ease ${0.1 + i * 0.1}s`,
                  minHeight: feature.size === 'large' ? '220px' : '200px',
                }}
              >
                {/* Corner glow on hover */}
                <div className="absolute top-0 right-0 w-32 h-32 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: 'radial-gradient(circle, rgba(0,255,0,0.1), transparent 70%)' }} />

                {/* Tag */}
                <div className="absolute top-4 right-4">
                  <span className="text-[#00FF00] text-xs font-mono uppercase tracking-widest opacity-50 group-hover:opacity-100 transition-opacity">
                    {feature.tag}
                  </span>
                </div>

                <div className="relative z-10 h-full flex flex-col">
                  <div className="mb-4 p-3 border border-[#1A1A1A] group-hover:border-[#00FF00] transition-colors duration-300 w-fit"
                    style={{ background: 'rgba(0,255,0,0.05)' }}>
                    <Icon size={22} color="#00FF00" strokeWidth={1.5} />
                  </div>

                  <h3 className="text-white font-bold mb-3 uppercase tracking-tight"
                    style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '13px', lineHeight: '1.4' }}>
                    {feature.title}
                  </h3>

                  <p className="text-[#A3A3A3] text-sm leading-relaxed flex-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {feature.description}
                  </p>

                  {feature.size === 'large' && (
                    <div className="mt-4 flex items-center gap-2 text-[#00FF00] text-xs font-mono uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      Learn more <ArrowRight size={12} />
                    </div>
                  )}
                </div>

                {/* Bottom indicator */}
                <div className="absolute bottom-0 left-0 w-0 h-[2px] bg-[#00FF00] group-hover:w-full transition-all duration-500" />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
